import { useState, useCallback, useRef, useEffect } from 'react';
import { Message } from '../components/chat/MessageBubble';
import { useTranslation } from '../i18n';
import {
  trackSoulMessageSent,
  trackContentSuggested,
  trackOldConversationViewed,
  trackSoulConversationCompleted,
  trackSoulConversationAbandoned,
  derivePrimaryAgent,
  mapAgentIdToName,
  mapContentCategoryToContentType,
} from '../services/analytics.service';

interface UseChatOptions {
  apiUrl?: string;
  userId?: string;
  token?: string;
  onError?: (error: Error) => void;
  onLocationRequest?: () => Promise<{ latitude: number; longitude: number } | null>;
}

// Abort an in-flight stream for one of three distinct reasons. The reason
// decides what happens next: 'user' and 'switch' silently stop, 'timeout'
// counts as a retryable failure.
type AbortReason = 'user' | 'switch' | 'timeout';

// If the server sends no SSE event for this long mid-stream, the attempt is
// aborted and retried (prevents the UI hanging forever on a dead connection).
const STREAM_IDLE_TIMEOUT_MS = 45000;

export const useChat = (options: UseChatOptions = {}) => {
  const {
    apiUrl = '/api',
    userId,
    token,
    onError,
    onLocationRequest,
  } = options;

  const { t, lang } = useTranslation();

  const [sessionId, setSessionId] = useState<string>('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [contentRecommendations, setContentRecommendations] = useState<any[]>([]);
  const [activeSkillId, setActiveSkillId] = useState<string | null>(null);
  const [skillSuggestions, setSkillSuggestions] = useState<string[]>([]);
  const [agentOverride, setAgentOverride] = useState<string>('best');
  const [isConversationEnded, setIsConversationEnded] = useState(false);
  const [mhWrapUp, setMhWrapUp] = useState(false);
  const [toneMode, setToneMode] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('soul-tone-mode') || 'clinical';
    }
    return 'friendly';
  });
  const abortControllerRef = useRef<AbortController | null>(null);
  const abortReasonRef = useRef<AbortReason | null>(null);

  // Monotonic local message id source. Date.now() alone can collide when two
  // messages are created in the same millisecond (send + streaming bubble,
  // double-tap sends, canned double greetings).
  const localIdRef = useRef<number>(Date.now());
  const nextLocalId = useCallback(() => {
    localIdRef.current += 1;
    return localIdRef.current;
  }, []);

  // Stream generation: bumped whenever the visible conversation is replaced
  // (session switch, clear, skill enter/exit). An in-flight stream from an
  // older generation must stop mutating state — otherwise its tokens, error
  // bubbles and isStreaming resets leak into the new conversation.
  const streamGenRef = useRef(0);

  // Keep latest messages in a ref so sendMessage doesn't need `messages` as a
  // dependency (it only peeks at the last assistant message for location).
  const messagesRef = useRef<Message[]>(messages);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Mirror the "ended naturally" flag into a ref so the unload listener below
  // reads the latest value without re-binding.
  const isConversationEndedRef = useRef(isConversationEnded);
  useEffect(() => {
    isConversationEndedRef.current = isConversationEnded;
  }, [isConversationEnded]);

  // Fire "Soul Conversation Abandoned" when the user leaves an in-progress
  // conversation without it reaching a natural close. We use `pagehide` (real
  // unload/tab close, incl. iOS/Android webviews) rather than visibilitychange
  // to avoid false positives from merely backgrounding the tab. Guarded so it
  // fires at most once per conversation and never when it already ended.
  const abandonedFiredRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const fireAbandonedIfNeeded = () => {
      const msgs = messagesRef.current;
      const turnNumber = msgs.filter((m) => m.role === 'user').length;
      if (turnNumber === 0) return; // no conversation to abandon
      if (isConversationEndedRef.current) return; // ended naturally → Completed handles it
      const key = sessionId || 'no-session';
      if (abandonedFiredRef.current.has(key)) return;
      abandonedFiredRef.current.add(key);
      try {
        const firstAt = msgs[0]?.createdAt ? new Date(msgs[0].createdAt).getTime() : Date.now();
        const durationSeconds = Math.max(0, Math.round((Date.now() - firstAt) / 1000));
        const agentType = derivePrimaryAgent(
          msgs.filter((m) => m.role === 'assistant').map((m) => m.agent)
        );
        trackSoulConversationAbandoned({ turnNumber, agentType, durationSeconds });
      } catch {
        // never let analytics break anything
      }
    };
    window.addEventListener('pagehide', fireAbandonedIfNeeded);
    return () => window.removeEventListener('pagehide', fireAbandonedIfNeeded);
  }, [sessionId]);

  // Abort any in-flight stream because the conversation is being replaced.
  const invalidateStream = useCallback(() => {
    streamGenRef.current += 1;
    if (abortControllerRef.current) {
      abortReasonRef.current = 'switch';
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsStreaming(false);
    setCurrentStreamingMessage('');
  }, []);

  // Initialize session on mount
  useEffect(() => {
    const controller = new AbortController();
    const initSession = async () => {
      if (!userId) {
        setIsInitializing(false);
        return;
      }

      try {
        const response = await fetch(`${apiUrl}/chat/sessions`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          signal: controller.signal,
          body: JSON.stringify({
            userId,
            metadata: {
              device: 'web',
              userAgent: navigator.userAgent,
            },
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to create session');
        }

        const data = await response.json();
        if (!controller.signal.aborted) {
          setSessionId(data.data.sessionId);
        }
      } catch (error) {
        if ((error as Error)?.name === 'AbortError') return;
        console.error('Error initializing session:', error);
        onError?.(error as Error);
      } finally {
        if (!controller.signal.aborted) {
          setIsInitializing(false);
        }
      }
    };

    initSession();
    return () => controller.abort();
  }, [apiUrl, userId, onError]);

  const sendMessage = useCallback(
    async (content: string, imageUrl?: string, overrideAgent?: string) => {
      if ((!content.trim() && !imageUrl) || isStreaming || !sessionId) return;

      const gen = streamGenRef.current;
      const isStale = () => streamGenRef.current !== gen;

      // Analytics context for "Soul Message Sent" (computed before the optimistic
      // update so turnNumber counts this message as the Nth user turn).
      const messageLength = content.trim().length;
      const turnNumber = messagesRef.current.filter((m) => m.role === 'user').length + 1;

      // Add user message immediately (optimistic update)
      const userMessage: Message = {
        id: nextLocalId(),
        role: 'user',
        content: content.trim(),
        createdAt: new Date(),
        imageUrl: imageUrl,
      };

      setMessages((prev) => [...prev, userMessage]);
      setIsStreaming(true);
      setCurrentStreamingMessage('');
      setSkillSuggestions([]);

      // Create temporary streaming message
      const streamingMessageId = nextLocalId();
      const streamingMessage: Message = {
        id: streamingMessageId,
        role: 'assistant',
        content: '',
        createdAt: new Date(),
        isStreaming: true,
      };

      setMessages((prev) => [...prev, streamingMessage]);

      // ─── Retry wrapper ───────────────────────────────────────────────────
      // Some requests never produce a response (flaky network, backend hiccup,
      // SSE dropped before first token). We retry up to 3 times TOTAL as long
      // as we received zero content. Any of these short-circuits retries:
      //   - user aborts the request / conversation is replaced
      //   - we got any token/recs/routing from the server
      //   - crisis / conversation_ended (intentional terminal states)
      const MAX_ATTEMPTS = 3;
      const RETRY_DELAYS_MS = [800, 1600]; // between attempts 1→2 and 2→3
      let attemptNumber = 0;
      let gotAnyContent = false;
      let terminalState = false; // crisis / end signals
      let lastError: Error | null = null;
      let aborted = false;

      // ─── Location handshake ──────────────────────────────────────────────
      // When the server can only answer with coordinates it emits
      // 'location_required' instead of a reply. We then trigger the OS
      // permission prompt and replay this same message — the user never has to
      // send a second message, and nothing is asked on chat open.
      let resolvedLocation: { latitude: number; longitude: number } | null = null;
      let isLocationRetry = false;
      let locationRequired = false;

      locationPass: while (true) {
      attemptLoop: while (attemptNumber < MAX_ATTEMPTS) {
        attemptNumber++;
        lastError = null;
        let idleTimer: ReturnType<typeof setTimeout> | null = null;
        try {
          // Create a FRESH abort controller each attempt so user cancel works
          // and retries don't reuse an aborted signal.
          const controller = new AbortController();
          abortControllerRef.current = controller;
          abortReasonRef.current = null;

          // Idle watchdog: aborts the attempt when the server goes silent.
          const armIdleTimer = () => {
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(() => {
              abortReasonRef.current = 'timeout';
              controller.abort();
            }, STREAM_IDLE_TIMEOUT_MS);
          };

          // Try to get user location ONLY when it is actually needed. Asking on
          // every chat (mental-health, nutrition, faq…) is intrusive and
          // confusing, so we gate it behind:
          //   1) explicit wellnesspass agent override, OR
          //   2) message text that clearly looks location-aware (gym/studio/
          //      class/yoga near me, etc.), OR
          //   3) the previous assistant message asked for it, OR
          //   4) the server signalled 'location_required' for this exact
          //      message — the authoritative case, already resolved below.
          // 1–3 are a fast path that saves a round-trip; 4 is the safety net
          // for wordings the keyword list cannot anticipate ("kürek tesisi").
          let location = resolvedLocation;
          const wellnessKeywords = /\b(yak[ıi]n[ıi]m|yak[ıi]nda|gym|spor salonu|stüdyo|stüdyolar|studio|studios|yoga|pilates|fitness|wellness ?pass|class|seans|yak[ıi]n[ıi]mdaki|nearby|near me|around me)\b/i;
          // Also request location if the last assistant message asked for it
          const locationRequestPatterns = ['konumuna ihtiyacım var', 'konum izni', 'şehir ve semttesin', 'need your location', 'city and neighborhood'];
          const lastAssistantMsg = messagesRef.current.filter((m) => m.role === 'assistant').pop();
          const assistantAskedForLocation = lastAssistantMsg && locationRequestPatterns.some((p) => lastAssistantMsg.content.toLowerCase().includes(p));
          const shouldAskLocation =
            !!onLocationRequest &&
            ((agentOverride === 'wellnesspass') ||
              wellnessKeywords.test(content) ||
              assistantAskedForLocation);
          // On the replay pass the permission prompt has already run — never
          // fire it twice for one message.
          if (!location && !isLocationRetry && shouldAskLocation && onLocationRequest) {
            try {
              location = await onLocationRequest();
            } catch {
              // Location unavailable — proceed without it.
            }
          }

          if (isStale()) return;

          armIdleTimer();
          const response = await fetch(`${apiUrl}/chat/message`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({
              sessionId,
              message: content.trim(),
              userId,
              location,
              imageUrl,
              token,
              skillId: activeSkillId,
              lang,
              agentOverride: overrideAgent ?? (agentOverride !== 'best' ? agentOverride : undefined),
              toneMode,
              locationRetry: isLocationRetry,
              // Tells the server this client can handle 'location_required'.
              // Without it the server keeps the old behaviour, so a cached
              // older bundle never ends up with an empty reply.
              supportsLocationPrompt: true,
            }),
            signal: controller.signal,
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          if (!response.body) {
            throw new Error('Response body is null');
          }

          // Read SSE stream. decode(…, {stream: true}) + a carry-over buffer
          // are both required: chunk boundaries can split multi-byte UTF-8
          // characters (Turkish text!) and can split SSE events, which the old
          // implementation corrupted / dropped.
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let sseBuffer = '';
          let accumulatedContent = '';
          let routedAgent = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            armIdleTimer();

            sseBuffer += decoder.decode(value, { stream: true });
            const events = sseBuffer.split('\n\n');
            sseBuffer = events.pop() || ''; // keep incomplete event for next chunk

            for (const event of events) {
              const line = event.split('\n').find((l) => l.startsWith('data: '));
              if (!line) continue;

              // Parse and handle separately: a handler throw (e.g. server
              // error event) must NOT be swallowed by the JSON-parse catch.
              let data: any;
              try {
                data = JSON.parse(line.slice(6));
              } catch (parseError: any) {
                console.error('[SSE Parse Error]', parseError?.message || parseError, '| line:', line.substring(0, 200));
                continue;
              }

              if (data.type === 'error' || data.error) {
                throw new Error(data.error || 'Server reported an error');
              }

              if (isStale()) return;

              // Handle routing info (new orchestrator system)
              if (data.type === 'routing') {
                routedAgent = data.agent || '';
                // Attach agent to streaming message immediately
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingMessageId
                      ? { ...msg, agent: routedAgent }
                      : msg
                  )
                );
                // Analytics: the server has decided which agent handles this
                // message — fire Soul Message Sent with the mapped agent name.
                // Skipped on a location replay: same user message, already sent.
                const agentName = isLocationRetry ? null : mapAgentIdToName(routedAgent);
                if (agentName)
                  trackSoulMessageSent(agentName, {
                    agentType: routedAgent || null,
                    conversationId: sessionId,
                    turnNumber,
                    messageLength,
                  });
              }

              if (data.type === 'token' && data.token) {
                gotAnyContent = true;
                accumulatedContent += data.token;
                setCurrentStreamingMessage(accumulatedContent);

                // Update streaming message
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingMessageId
                      ? { ...msg, content: accumulatedContent }
                      : msg
                  )
                );
              }

              if (data.type === 'recommendations') {
                gotAnyContent = true;
                const recs = data.recommendations || [];
                setContentRecommendations(recs);
                // Analytics: fire Content Suggested once per recommendation.
                for (const rec of recs) {
                  trackContentSuggested(
                    mapContentCategoryToContentType(rec?.content_type),
                    rec?.content_id || rec?.id || null
                  );
                }
                // Attach recommendations to the current streaming message so they render inline.
                // Backend persists the payload itself (atomic with SSE send) — no FE save call needed.
                if (recs.length > 0) {
                  setMessages((prev) =>
                    prev.map((msg) =>
                      msg.id === streamingMessageId
                        ? { ...msg, recommendations: recs }
                        : msg
                    )
                  );
                }
              }

              if (data.type === 'skill_suggestions') {
                setSkillSuggestions(data.suggestions || []);
              }

              // Server needs coordinates to answer this message. No text
              // follows — the handshake is resolved after the stream closes.
              if (data.type === 'location_required') {
                locationRequired = true;
              }

              if (data.type === 'crisis_alert') {
                terminalState = true;
                gotAnyContent = true;
                setIsConversationEnded(true);
                // Replace streaming message with crisis alert
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingMessageId
                      ? { ...msg, content: data.message, crisisAlert: true, crisisNotice: data.notice, isStreaming: false }
                      : msg
                  )
                );
              }

              if (data.type === 'mh_wrap_up') {
                setMhWrapUp(true);
              }

              if (data.type === 'conversation_ended') {
                terminalState = true;
                setIsConversationEnded(true);
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingMessageId
                      ? { ...msg, conversationSummary: data.summary }
                      : msg
                  )
                );
                // Analytics: conversation reached a natural close.
                try {
                  const msgs = messagesRef.current;
                  const totalTurns = msgs.filter((m) => m.role === 'user').length;
                  const firstAt = msgs[0]?.createdAt
                    ? new Date(msgs[0].createdAt).getTime()
                    : Date.now();
                  const durationSeconds = Math.max(
                    0,
                    Math.round((Date.now() - firstAt) / 1000)
                  );
                  const primaryAgent = derivePrimaryAgent(
                    msgs.filter((m) => m.role === 'assistant').map((m) => m.agent)
                  );
                  trackSoulConversationCompleted({
                    totalTurns,
                    durationSeconds,
                    agentType: primaryAgent,
                  });
                } catch {
                  // never let analytics break the stream
                }
              }

              if (data.type === 'done') {
                // Location handshake pass: the real reply is still coming on
                // the replay, so keep the bubble in its streaming state.
                if (locationRequired) continue;
                // Finalize message - keep id stable to prevent React remount
                setMessages((prev) =>
                  prev.map((msg) =>
                    msg.id === streamingMessageId
                      ? {
                          ...msg,
                          dbId: data.messageId || streamingMessageId,
                          isStreaming: false,
                        }
                      : msg
                  )
                );
              }
            }
          }
          // Stream finished without throwing — success. Exit retry loop.
          break attemptLoop;
        } catch (error) {
          if (error instanceof Error) {
            if (error.name === 'AbortError') {
              if (abortReasonRef.current === 'timeout') {
                // Watchdog fired — retryable like any network failure.
                lastError = new Error('Stream timed out');
                console.error(`[sendMessage] attempt ${attemptNumber}/${MAX_ATTEMPTS} timed out after ${STREAM_IDLE_TIMEOUT_MS}ms of silence`);
              } else {
                // 'user' cancel or 'switch' (conversation replaced) — stop silently.
                aborted = true;
                break attemptLoop;
              }
            } else {
              lastError = error;
              console.error(`[sendMessage] attempt ${attemptNumber}/${MAX_ATTEMPTS} failed:`, error.message);
            }
          }

          // Don't retry if we already have partial content — retrying would
          // produce a duplicate or conflicting stream. Also don't retry past
          // a terminal signal (crisis/end) or a replaced conversation.
          if (gotAnyContent || terminalState || isStale()) break attemptLoop;

          // If there are attempts left, wait then loop.
          if (attemptNumber < MAX_ATTEMPTS) {
            const delay = RETRY_DELAYS_MS[attemptNumber - 1] || 2000;
            await new Promise((r) => setTimeout(r, delay));
            if (isStale()) return;
          }
        } finally {
          if (idleTimer) clearTimeout(idleTimer);
        }
      } // end attemptLoop

      // Resolve the location handshake: ask for the OS permission at the exact
      // moment it is needed, then replay the message. A denial is a valid
      // outcome — the replay runs without coordinates and the agent falls back
      // to asking which city/neighborhood the user is in. Runs at most once.
      if (locationRequired && !isLocationRetry && !aborted && !isStale()) {
        isLocationRetry = true;
        try {
          resolvedLocation = onLocationRequest ? await onLocationRequest() : null;
        } catch {
          resolvedLocation = null;
        }
        if (isStale()) return;
        locationRequired = false;
        attemptNumber = 0;
        gotAnyContent = false;
        terminalState = false;
        lastError = null;
        continue locationPass;
      }
      break;
      } // end locationPass

      // A replaced conversation (session switch / clear) owns the state now —
      // don't touch messages or streaming flags from this stale closure.
      if (isStale()) return;

      // Report failure to the user only if every attempt failed without any
      // content coming through. Aborted requests stay silent.
      if (lastError && !gotAnyContent && !aborted) {
        onError?.(lastError);
        setMessages((prev) => prev.filter((msg) => msg.id !== streamingMessageId));
        const errorMessage: Message = {
          id: nextLocalId(),
          role: 'assistant',
          content: t('chat.errorMessage'),
          createdAt: new Date(),
        };
        setMessages((prev) => [...prev, errorMessage]);
      } else if (aborted) {
        // User cancelled — drop the empty streaming bubble.
        setMessages((prev) => prev.filter((msg) => msg.id !== streamingMessageId || msg.content.trim() !== ''));
      }

      setIsStreaming(false);
      setCurrentStreamingMessage('');
      abortControllerRef.current = null;
    },
    [apiUrl, sessionId, userId, token, lang, toneMode, t, isStreaming, onError, onLocationRequest, activeSkillId, agentOverride, nextLocalId]
  );

  const cancelStreaming = useCallback(() => {
    if (abortControllerRef.current) {
      abortReasonRef.current = 'user';
      abortControllerRef.current.abort();
    }
  }, []);

  const clearMessages = useCallback(async () => {
    invalidateStream();
    setMessages([]);
    setContentRecommendations([]);
    setSkillSuggestions([]);
    setIsConversationEnded(false);
    setMhWrapUp(false);

    // Create a fresh session so old context doesn't leak
    if (userId) {
      try {
        const response = await fetch(`${apiUrl}/chat/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userId,
            metadata: { device: 'web', userAgent: navigator.userAgent },
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setSessionId(data.data.sessionId);
        } else {
          throw new Error('Failed to create new session');
        }
      } catch (e) {
        console.error('Error creating new session:', e);
        onError?.(e as Error);
      }
    }
  }, [apiUrl, userId, onError, invalidateStream]);

  const clearRecommendations = useCallback(() => {
    setContentRecommendations([]);
  }, []);

  const addAssistantMessage = useCallback((content: string, options?: { hideActions?: boolean }) => {
    const assistantMessage: Message = {
      id: nextLocalId(),
      role: 'assistant',
      content: content,
      createdAt: new Date(),
      hideActions: options?.hideActions,
    };
    setMessages((prev) => [...prev, assistantMessage]);
  }, [nextLocalId]);

  const startSkillSession = useCallback(async (skillId: string, welcomeMessage?: string, initialSuggestions?: string[]) => {
    if (!userId) return;

    invalidateStream();
    try {
      const response = await fetch(`${apiUrl}/chat/sessions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          userId,
          metadata: { skillId, device: 'web' },
        }),
      });

      if (!response.ok) throw new Error('Failed to create skill session');

      const data = await response.json();
      setSessionId(data.data.sessionId);
      setActiveSkillId(skillId);
      setContentRecommendations([]);

      // Show AI welcome message instantly
      if (welcomeMessage) {
        const welcome: Message = {
          id: nextLocalId(),
          role: 'assistant',
          content: welcomeMessage,
          createdAt: new Date(),
        };
        setMessages([welcome]);
      } else {
        setMessages([]);
      }

      // Show initial quick-reply suggestions
      setSkillSuggestions(initialSuggestions || []);
    } catch (error) {
      console.error('Error starting skill session:', error);
      onError?.(error as Error);
    }
  }, [apiUrl, userId, onError, invalidateStream, nextLocalId]);

  const exitSkillMode = useCallback(async () => {
    invalidateStream();
    setActiveSkillId(null);
    setMessages([]);
    setContentRecommendations([]);
    setSkillSuggestions([]);
    setIsConversationEnded(false);
    setMhWrapUp(false);

    // Create a new normal session (skill session should not carry over)
    if (userId) {
      try {
        const response = await fetch(`${apiUrl}/chat/sessions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userId,
            metadata: { device: 'web', userAgent: navigator.userAgent },
          }),
        });
        if (response.ok) {
          const data = await response.json();
          setSessionId(data.data.sessionId);
        } else {
          throw new Error('Failed to create session after skill exit');
        }
      } catch (e) {
        console.error('Error creating session after skill exit:', e);
        onError?.(e as Error);
      }
    }
  }, [apiUrl, userId, onError, invalidateStream]);

  const loadMessages = useCallback(async (loadSessionId: string) => {
    invalidateStream();
    try {
      const response = await fetch(`${apiUrl}/chat/history/${loadSessionId}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to load session history');
      }

      const data = await response.json();

      const HIDDEN_PREFIXES = ['[RECOMMENDED CONTENT]', '[WELLNESSPASS RECOMMENDATIONS]'];
      const loadedMessages: Message[] = data.data.messages
        .filter((msg: any) => {
          if (msg.role === 'system') return false;
          const c: string = msg.content || '';
          return !HIDDEN_PREFIXES.some((p) => c.trimStart().startsWith(p));
        })
        .map((msg: any) => ({
          id: msg.id,
          role: msg.role,
          content: msg.content,
          createdAt: new Date(msg.created_at),
          imageUrl: msg.image_url,
        }));

      setMessages(loadedMessages);
      setSessionId(loadSessionId);
      setContentRecommendations([]);
      setSkillSuggestions([]);

      // Analytics: old conversation opened via sidebar.
      const sessionDate =
        data.data?.session?.started_at ||
        data.data?.session?.created_at ||
        loadedMessages[0]?.createdAt?.toISOString?.() ||
        null;
      trackOldConversationViewed(sessionDate);
    } catch (error) {
      console.error('Error loading messages:', error);
      onError?.(error as Error);
      throw error; // let callers show their own failure UI
    }
  }, [apiUrl, onError, invalidateStream]);

  return {
    sessionId,
    messages,
    isStreaming,
    isInitializing,
    currentStreamingMessage,
    contentRecommendations,
    activeSkillId,
    skillSuggestions,
    agentOverride,
    setAgentOverride,
    isConversationEnded,
    mhWrapUp,
    setMhWrapUp,
    toneMode,
    setToneMode: (mode: string) => {
      setToneMode(mode);
      if (typeof window !== 'undefined') {
        localStorage.setItem('soul-tone-mode', mode);
      }
    },
    sendMessage,
    cancelStreaming,
    clearMessages,
    clearRecommendations,
    addAssistantMessage,
    loadMessages,
    startSkillSession,
    exitSkillMode,
  };
};
